import { captureException } from '@sentry/react-router'
import { useEffect, type ReactElement } from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from 'react-router'
import { getErrorMessage } from '#app/utils/misc.tsx'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

function formatErrorMessage(error: ErrorResponse) {
	if (error.status === 403) {
		const deniedMessage =
			typeof error.data === 'object' &&
			error.data &&
			'message' in error.data &&
			typeof error.data.message === 'string'
				? error.data.message
				: null

		return `Accès refusé : ${
			deniedMessage || "vous n'êtes pas autorisé à accéder à cette ressource."
		}`
	}

	if (typeof error.data === 'string') {
		return error.data
	}

	if (
		error.data &&
		typeof error.data === 'object' &&
		'message' in error.data &&
		typeof error.data.message === 'string'
	) {
		return error.data.message
	}

	try {
		return JSON.stringify(error.data)
	} catch (e) {
		console.error('Failed to stringify route error data', e)
		return getErrorMessage(error)
	}
}

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<div className='space-y-1 text-center'>
			<p className='text-lg font-semibold'>{error.status}</p>
			<p className='text-muted-foreground'>{formatErrorMessage(error)}</p>
		</div>
	),
	statusHandlers,
	unexpectedErrorHandler = (error) => <p>{getErrorMessage(error)}</p>,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	const error = useRouteError()
	const params = useParams()
	const isResponse = isRouteErrorResponse(error)

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	useEffect(() => {
		if (isResponse) return

		captureException(error)
	}, [error, isResponse])

	return (
		<div className='text-h2 container flex items-center justify-center p-20'>
			{isResponse
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: unexpectedErrorHandler(error)}
		</div>
	)
}
